import { useEffect, useState } from "react";
import { BadgeCheck, ChevronDown, ExternalLink, Wallet2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* "Benefits Available for This Trip" — rules-based matches per flight segment from the
   viewer's own Benefits Wallet. Suggestions only, never guaranteed eligibility. */
export default function TripBenefits({ trip }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  const load = () => api.get(`/wallet/trip-match/${trip.id}`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [trip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;
  const total = data.segments.reduce((n, s) => n + s.matches.length, 0);
  if (!data.has_wallet && total === 0) {
    return (
      <section className="mb-10" data-testid="trip-benefits-empty">
        <h2 className="font-heading font-900 text-emerald-700 text-2xl flex items-center gap-2 mb-2"><Wallet2 className="w-6 h-6" /> Benefits Available for This Trip</h2>
        <p className="text-sm text-muted-foreground">Add your loyalty programs, certificates, credits and cards to your <a href="/travel-hub#wallet" className="text-primary hover:underline">Benefits Wallet</a> and we'll automatically show which perks you may be able to use on this trip.</p>
      </section>
    );
  }

  const applyToTrip = async (m) => {
    if (m.kind === "account") return;
    try {
      await api.post(`/wallet/items/${m.kind}/${m.id}/apply`, { trip_id: trip.id });
      toast.success("Attached to this trip — not marked used until you confirm redemption");
      load();
    } catch { toast.error("Could not attach"); }
  };

  return (
    <section className="mb-10" data-testid="trip-benefits">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 mb-3 text-left">
        <h2 className="font-heading font-900 text-emerald-700 text-2xl flex items-center gap-2"><Wallet2 className="w-6 h-6" /> Benefits Available for This Trip</h2>
        {total > 0 && <span className="bg-emerald-100 text-emerald-800 text-xs font-700 px-2 py-0.5 rounded-full" data-testid="trip-benefits-count">{total}</span>}
        <ChevronDown className={`w-5 h-5 ml-auto text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          {data.segments.length === 0 && <p className="text-sm text-muted-foreground">Add flight segments to this trip and we'll match your wallet benefits to each leg.</p>}
          <div className="space-y-4">
            {data.segments.map((s, i) => (
              <div key={s.segment.id || i} className="border border-zinc-300 bg-white" data-testid={`trip-benefits-seg-${i}`}>
                <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex items-center gap-2">
                  <span className="font-600 text-sm">{s.segment.label}</span>
                  {s.segment.date && <span className="text-xs font-mono text-muted-foreground">{s.segment.date}</span>}
                  <span className="ml-auto text-xs text-muted-foreground">{s.matches.length} match{s.matches.length === 1 ? "" : "es"}</span>
                </div>
                {s.matches.length === 0 ? (
                  <p className="px-4 py-3 text-xs text-muted-foreground">No stored benefits matched this leg.</p>
                ) : (
                  <ul className="divide-y divide-zinc-100">
                    {s.matches.map((m, j) => (
                      <li key={`${m.kind}-${m.id}`} className="px-4 py-2.5 flex items-start gap-2.5" data-testid={`trip-benefit-match-${i}-${j}`}>
                        <BadgeCheck className={`w-4 h-4 mt-0.5 shrink-0 ${m.confidence === "likely" ? "text-emerald-600" : "text-amber-500"}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-600 flex items-center gap-2 flex-wrap">
                            {m.title}
                            <span className={`text-[10px] font-700 uppercase tracking-wider px-1.5 py-0.5 rounded ${m.confidence === "likely" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                              {m.confidence === "likely" ? "Likely" : "May be eligible"}
                            </span>
                            {m.applied && <span className="text-[10px] font-700 uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Applied</span>}
                          </div>
                          <div className="text-xs text-muted-foreground">{m.why}{m.expiration_date ? ` · expires ${String(m.expiration_date).slice(0, 10)}` : ""}</div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          {m.kind !== "account" && !m.applied && (
                            <button onClick={() => applyToTrip(m)} data-testid={`trip-benefit-apply-${i}-${j}`} className="text-xs border border-zinc-300 px-2 py-1 hover:border-emerald-500 text-emerald-700">Apply to trip</button>
                          )}
                          {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="text-xs border border-zinc-300 px-2 py-1 hover:border-primary text-primary inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Terms</a>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          {data.reminders.length > 0 && (
            <div className="mt-3 border border-amber-300 bg-amber-50/60 p-3" data-testid="trip-benefits-reminders">
              <div className="text-sm font-700 text-amber-800 mb-1">Worth checking before / around this trip</div>
              <ul className="text-xs text-amber-800/90 space-y-0.5">
                {data.reminders.map((r) => <li key={`${r.kind}-${r.id}`}>• <b>{r.title}</b> — {r.note}</li>)}
              </ul>
            </div>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground">{data.disclaimer}</p>
        </>
      )}
    </section>
  );
}
